import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import InvoiceClient from '../../api/invoiceClient';

const PAGE_SIZE = 25;

// Same DataTableCard + server page/total pattern as the admin Invoices list
// (BACKEND.md §1), plus the billing KPIs/6-month chart from GET
// /metrics/billing?userId= (BACKEND.md §3) — no client-side metric maths.
const useMyInvoicesPage = () => {
  const { t, i18n } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [userId] = useState(() => localStorage.getItem('userId') || '');
  const invoiceClient = useMemo(() => (
    config?.backendUrl ? new InvoiceClient(config.backendUrl) : null
  ), [config]);

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [selection, setSelection] = useState([]);

  const [billing, setBilling] = useState(null);
  const [billingStatus, setBillingStatus] = useState('loading');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchInvoices = useCallback((reset = true) => {
    if (!invoiceClient) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const filters = { page, pageSize: PAGE_SIZE };
    if (statusFilter !== 'all') filters.status = statusFilter;
    invoiceClient.getMyInvoices(filters)
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching invoices', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /invoices/my · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [invoiceClient, statusFilter, t]);

  useEffect(() => { fetchInvoices(true); }, [fetchInvoices]);

  const fetchBilling = useCallback(() => {
    if (!config?.backendUrl || !userId) return;
    setBillingStatus('loading');
    axios.get(`${config.backendUrl}/metrics/billing`, { params: { userId } })
      .then((res) => {
        setBilling(res.data);
        setBillingStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching billing metrics', error);
        setBillingStatus('error');
      });
  }, [config, userId]);

  useEffect(() => { fetchBilling(); }, [fetchBilling]);

  const loadMore = () => fetchInvoices(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  const selectedInvoices = useMemo(() => rows.filter((inv) => selection.includes(inv._id)), [rows, selection]);

  const downloadPdf = async (invoice) => {
    if (!invoiceClient) return;
    try {
      const res = await invoiceClient.downloadMyInvoicePdf(invoice._id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber || 'invoice'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF', error);
      notify(error.response?.data?.error || t('Error downloading PDF.'), 'error');
    }
  };

  const downloadSelectedPdfs = async () => {
    for (const invoice of selectedInvoices) {
      // eslint-disable-next-line no-await-in-loop
      await downloadPdf(invoice);
    }
    notify(t('{{count}} invoice(s) downloaded', { count: selectedInvoices.length, defaultValue: `${selectedInvoices.length} invoice(s) downloaded` }));
    setSelection([]);
  };

  const isPreviewEmptyKind = previewState === 'empty';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? rows : []);

  return {
    t, i18n, effectiveStatus, effectiveRows, total, errorInfo,
    statusFilter, setStatusFilter,
    selection, setSelection, selectedInvoices,
    loadingMore, loadMore, remaining, nextBatchSize,
    billing, billingStatus,
    downloadPdf, downloadSelectedPdfs,
    snackbar, setSnackbar,
    fetchInvoices,
  };
};

export default useMyInvoicesPage;
